import { ApiError, ErrorCode } from "fe-shared/src/types/common";

// The client maps this error to a translated collector-field message.
export const ONGOING_PICKUP_ERROR_CODE = 448 as ErrorCode;

export const isOngoingPickupError = (
  apiError: ApiError | undefined
): boolean => apiError?.errorCode === ONGOING_PICKUP_ERROR_CODE;

interface ValidationError {
  fullFieldName: string;
  description: string;
}

type ApiErrorWithDetails = ApiError & { additionalInfo?: ValidationError[] };

interface FieldToken {
  pattern: RegExp;
  field: string;
}

const FIELD_TOKENS: FieldToken[] = [
  { pattern: /^sourceCompanyId\b/i, field: "collectorId" },
  { pattern: /^targetCompanyId\b/i, field: "haulerId" },
  { pattern: /^pickupDateFrom\b/i, field: "pickupDateFrom" },
  { pattern: /^pickupDateTo\b/i, field: "pickupDateTo" },
  { pattern: /^materials?\b/i, field: "materials" },
  { pattern: /^otherReasonText\b/i, field: "otherReasonText" },
  { pattern: /^reasonId\b/i, field: "reasonIds" },
  { pattern: /^comment\b/i, field: "comment" },
  { pattern: /^priorityReason\b/i, field: "priorityReason" },
  { pattern: /^priority\b/i, field: "priority" },
];

const FIELD_ALIASES: Record<string, string> = {
  sourceCompanyId: "collectorId",
  targetCompanyId: "haulerId",
};

const toCamelCase = (segment: string) =>
  segment.charAt(0).toLowerCase() + segment.slice(1);

const toFormField = (fullFieldName: string) => {
  const key = fullFieldName.split(".").map(toCamelCase).join(".");

  return FIELD_ALIASES[key] ?? key;
};

export const mapApiErrorToServerErrors = (
  apiError: ApiErrorWithDetails | undefined
): Record<string, string> => {
  const validationErrors = apiError?.additionalInfo;

  if (validationErrors?.length) {
    return validationErrors.reduce<Record<string, string>>((result, error) => {
      result[toFormField(error.fullFieldName)] = error.description;
      return result;
    }, {});
  }

  if (!apiError?.message) {
    return {};
  }

  const token = FIELD_TOKENS.find(({ pattern }) =>
    pattern.test(apiError.message)
  );

  return token ? { [token.field]: apiError.message } : {};
};

export default mapApiErrorToServerErrors;
